import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import LoadingState from './LoadingState';
import {uiMessage} from './uiText';

const examples = ["帮我查看 GJB2 基因的变异配对网络", "查询 GJB2 基因的 c.109G>A 位点", "Show the variant-pair network for Pendred syndrome"];

export default function IntentAssistant({ mode, version, unitId, contextKey, onReady, onManual,onExample,onBusyChange,getView,helpRef }) {
    const [query, setQuery] = useState('');
    const [busy, setBusy] = useState(false);
    const [reply, setReply] = useState('');
    const [tone, setTone] = useState('info');
    const request = useRef(null);
    const sequence = useRef(0);
    const input = useRef(null);
    const [pendingExample, setPendingExample] = useState(false);
    const submitCurrent = useRef(null);
    const executing=useRef(false),[running,setRunning]=useState(false);
    useEffect(() => () => { sequence.current += 1; request.current?.abort(); }, []);
    useEffect(() => { if(executing.current)return;sequence.current += 1; request.current?.abort(); setBusy(false); setReply(''); }, [version, unitId, contextKey]);
    useEffect(() => {
        if (!pendingExample) return;
        setPendingExample(false);
        // The parent reset and example text have committed before reading the view.
        submitCurrent.current();
    }, [pendingExample]);
    useEffect(() => { onBusyChange?.(busy); }, [busy, onBusyChange]);
    function manual() {
        sequence.current += 1; request.current?.abort(); setPendingExample(false); setBusy(false); setReply(''); setQuery(''); onManual();
    }
    function chooseExample(example) {
        if (busy || pendingExample) return;
        sequence.current += 1; request.current?.abort(); setBusy(false); setReply('');
        onExample?.(); setQuery(example); setPendingExample(true); input.current?.focus({ cursor: 'end' });
    }
    async function submit() {
        if (!query.trim() || busy) return;
        const id = ++sequence.current;
        request.current?.abort(); const abort = new AbortController(); request.current = abort;
        setBusy(true); setReply(''); setTone('info');
        try {
            let response;
            for (let attempt = 1; attempt <= 3; attempt += 1) {
                try {
                    response = await axios.post('/api/rgnet/intent', { query: query.trim(), ...(version ? { datasetVersion: version } : {}), ...(unitId ? { unitId } : {}),...(getView?{view:getView()}: {}) }, {
                        headers: { Authorization: localStorage.getItem('userInfo') || '' }, signal: abort.signal, timeout: 70000,
                    });
                    break;
                } catch (ex) {
                    if (abort.signal.aborted || axios.isCancel(ex) || ex.response || ex.code === 'ECONNABORTED') throw ex;
                    if (attempt === 3) {
                        if (id === sequence.current) onManual("The recognition service could not be reached after three attempts. Switched to manual mode.");
                        return;
                    }
                }
            }
            if (abort.signal.aborted || id !== sequence.current) return;
            if (response.data.code !== 200) throw new Error(response.data.msg || "The recognition service is unavailable");
            const data = response.data.data;
            setReply(uiMessage(data.message,"Add or refine your query."));
            setTone(data.status === 'ready' ? 'success' : 'warning');
            if (data.status === 'ready') {
                executing.current=true;setRunning(true);
                try {
                    const result=await onReady(data,abort.signal);
                    if(!abort.signal.aborted&&id===sequence.current){
                        const message=typeof result==='string'?result:result?.message;
                        if(message)setReply(message);
                        if(result?.completed!==false)setQuery('');
                    }
                }finally{executing.current=false;setRunning(false);}
            }
            else if (data.status === 'manual') { setReply(''); onManual(uiMessage(data.message,"Recognition was not completed. Manual mode is available.")); }
        } catch (ex) {
            if (abort.signal.aborted || id !== sequence.current || axios.isCancel(ex)||ex.name==='AbortError') return;
            setTone('error'); setReply(ex.response?.status === 401 ? "Your session has expired. Please sign in again." : uiMessage(ex.response?.data?.msg || ex.message,"Cannot reach the query service. Retry or use manual mode."));
        } finally { if (!abort.signal.aborted && id === sequence.current) setBusy(false); }
    }
    submitCurrent.current = submit;
    const content = <div className="rgnet-intent-content" aria-busy={busy}>
        {mode === 'entry' && <><div className="rgnet-eyebrow">Recessive genotype networks</div><h2>Search RGNET</h2></>}
        <p className="rgnet-intent-guide"><strong>Describe what you want to explore</strong>. Search by disease, gene, variant or phenotype, then refine, compare or save your query.</p>
        <div className="rgnet-intent-search"><Input ref={input} id="rgnet-intent-query" aria-label={"Query"} value={query} onChange={event => setQuery(event.target.value)}
            prefix={<SearchOutlined />} size="large" placeholder={"For example: show the GJB2 variant-pair network"} maxLength={2000} disabled={busy}
            onPressEnter={submit} allowClear /><Button aria-label={"Search"} type="primary" size="large" loading={busy} disabled={busy || !query.trim()} onClick={submit}>Search</Button></div>
        {busy && <LoadingState title={running?"Running…":"Searching…"} description={running?"Please wait for the current action to finish.":"Matching diseases, genes and variants in the database."} timed />}
        <details ref={helpRef} className="rgnet-intent-help" open={mode==='entry'?true:undefined}><summary>Query examples</summary><div className="rgnet-intent-examples" aria-label={"Natural-language query examples"}><span>Try: </span>{examples.map(example => <button type="button" key={example} disabled={busy} onClick={() => chooseExample(example)}>{example}</button>)}</div><p className="rgnet-caption">Selecting an example clears the previous conditions and runs it automatically. Results update here. For follow-up commands, type “Show P only”, “Open network records”, “Download PNG” or “Save this query as hearing loss”; other conditions stay unchanged.</p></details>
        <div className="rgnet-intent-actions">
            <Button type="link" onClick={manual}>{mode === 'manual' ? "Clear manual query" : "Use manual mode"}</Button></div>
        {reply && <Alert type={tone} showIcon message={reply} />}
    </div>;
    return <section className={`rgnet-panel ${mode === 'entry' ? 'rgnet-intent-entry' : 'rgnet-intent-inline'}`}
        aria-label={mode === 'entry' ? "RGNET search entry" : "RGNET query assistant"}>{content}</section>;
}
